import { format } from 'date-fns'
import { useDisclosure } from '@ritualco/jutsu'
import {
  EditableSection,
  EditableSectionTerm,
  EditableSectionTermRow,
  EditableSectionValue,
} from 'components/EditableSection'
import {
  IncentiveDetailsModal,
  IncentiveDetailsModalOnSubmit,
} from 'components/Modals/IncentiveDetailsModal'
import { useContent } from 'lib/content/react'
import { ReactComponent as EditIcon } from 'assets/Icons/Edit.svg'
import {
  IncentiveDetails,
  IncentivePlanSchedule,
  IncentiveType,
} from 'types/entities'

const CREDIT_TYPES: IncentiveType[] = ['creditOneTime', 'creditRecurring']

const incentiveTypeMap: Record<IncentiveType, string> = {
  creditOneTime: 'pmportal.incentives.creditOneTimeType',
  creditRecurring: 'pmportal.incentives.creditRecurringType',
  discountAmount: 'pmportal.incentives.discountType',
  discountPercentage: 'pmportal.incentives.discountType',
}

interface DetailsSectionProps {
  incentiveDetails?: IncentiveDetails
  currencyCode: string
  timezone: string
  onSubmit: IncentiveDetailsModalOnSubmit
}

export const DetailsSection = ({
  incentiveDetails,
  currencyCode,
  timezone,
  onSubmit,
}: DetailsSectionProps) => {
  const { formatMessage } = useContent()
  const { isOpen, onOpen, onClose } = useDisclosure()
  return (
    <>
      <EditableSection
        title={'Incentive'}
        onEditClick={onOpen}
        icon={<EditIcon style={{ height: 18, width: 18 }} />}
      >
        {incentiveDetails?.name && (
          <EditableSectionTermRow>
            <EditableSectionTerm>Title</EditableSectionTerm>
            <EditableSectionValue>{incentiveDetails.name}</EditableSectionValue>
          </EditableSectionTermRow>
        )}
        {incentiveDetails?.description && (
          <EditableSectionTermRow>
            <EditableSectionTerm>Description</EditableSectionTerm>
            <EditableSectionValue>
              {incentiveDetails.description}
            </EditableSectionValue>
          </EditableSectionTermRow>
        )}
        <EditableSectionTermRow>
          <EditableSectionTerm>Incentive type</EditableSectionTerm>
          <EditableSectionValue>
            {formatMessage({
              id: incentiveDetails?.type
                ? incentiveTypeMap[incentiveDetails.type]
                : 'pmportal.incentives.selectAType',
            })}
          </EditableSectionValue>
        </EditableSectionTermRow>
        {incentiveDetails?.type &&
          CREDIT_TYPES.includes(incentiveDetails.type) && (
            <DisplayOneTimeCreditInfo
              currencyCode={incentiveDetails.currencyCode}
              amount={incentiveDetails.creditAmount}
              schedule={incentiveDetails.schedule}
            />
          )}
      </EditableSection>
      <IncentiveDetailsModal
        isOpen={isOpen}
        onClose={onClose}
        incentiveDetails={incentiveDetails}
        defaultCurrencyCode={currencyCode}
        defaultTimezone={timezone}
        onSubmit={onSubmit}
      />
    </>
  )
}

interface DisplayOneTimeCreditInfoProps {
  schedule?: IncentivePlanSchedule
  currencyCode?: string
  amount?: string
}

const DisplayOneTimeCreditInfo = ({
  schedule,
  currencyCode,
  amount,
}: DisplayOneTimeCreditInfoProps) => {
  const formatDate = (d: Date) => format(d, 'MMM d, yyyy')
  const getFrequency = () => {
    switch (schedule?.type) {
      case 'none':
        return 'Does not repeat'
      default:
        const frequency = schedule?.type as string
        return (
          frequency.charAt(0).toUpperCase() + frequency.substr(1).toLowerCase()
        )
    }
  }
  return (
    <>
      <EditableSectionTermRow>
        <EditableSectionTerm>Amount</EditableSectionTerm>
        <EditableSectionValue>
          {currencyCode} {amount}
        </EditableSectionValue>
      </EditableSectionTermRow>
      <EditableSectionTermRow>
        <EditableSectionTerm>Starts on</EditableSectionTerm>
        <EditableSectionValue>
          {formatDate(schedule?.start as Date)}
        </EditableSectionValue>
      </EditableSectionTermRow>
      <EditableSectionTermRow>
        <EditableSectionTerm>Frequency</EditableSectionTerm>
        <EditableSectionValue>{getFrequency()}</EditableSectionValue>
      </EditableSectionTermRow>
      {schedule?.fromTime && schedule.toTime && (
        <EditableSectionTermRow>
          <EditableSectionTerm>Time Available</EditableSectionTerm>
          <EditableSectionValue>
            {schedule.fromTime} - {schedule.toTime}
          </EditableSectionValue>
        </EditableSectionTermRow>
      )}
      <EditableSectionTermRow>
        <EditableSectionTerm>Ends</EditableSectionTerm>
        <EditableSectionValue>
          {schedule?.ending === 'never'
            ? 'Never'
            : formatDate(schedule?.endingOn as Date)}
        </EditableSectionValue>
      </EditableSectionTermRow>
    </>
  )
}
